import { LocalDate } from 'src/core/common/domain/date/local-date';
import { BadRequestException } from 'src/core/common/domain/exceptions/bad-request-exception';
import { InternalServerException } from 'src/core/common/domain/exceptions/internal-server-exception';

export enum QueueElementType {
  START = 'start',
  STOP = 'stop',
  CLOSE = 'close',
  CONFIRM_CLOSE = 'confirm_close',
  REJECT = 'reject',
  CONFIRM_REJECT = 'confirm_reject',
  CREATE = 'create',
  MARK_NOTIFICATION_READ = 'mark_notification_read',
}

export type QueueElementError = {
  parameter: string;
  reason: string;
  message: string;
};

export abstract class QueueElement {
  label: string;
  publicId: string;
  errors: Array<QueueElementError>;

  protected constructor(
    public readonly id: string,
    public readonly createdAt: LocalDate,
    public readonly type: QueueElementType
  ) {}

  static fromPayload(_payload: any) {
    throw new Error('You need to implement fromPayload');
  }

  static getInternalServerExceptionError(): QueueElementError {
    return {
      parameter: 'none',
      reason: '500',
      message: 'Internal Server Exception',
    };
  }

  /**
   * What the synchronization list shows for a failed retry. A 400 without errors in its body, or any other
   * failure, still needs to show something, or the element looks pending while it fails every time.
   */
  static errorsFromException(exception: unknown): Array<QueueElementError> {
    if (exception instanceof InternalServerException) {
      return [QueueElement.getInternalServerExceptionError()];
    }

    const badRequestErrors = exception instanceof BadRequestException ? exception.getErrors() : [];
    if (badRequestErrors.length > 0) {
      return badRequestErrors;
    }

    return [QueueElement.getUnexpectedError(exception)];
  }

  static getUnexpectedError(exception: unknown): QueueElementError {
    return {
      parameter: 'none',
      reason: 'unexpected',
      message: exception instanceof Error ? exception.message : String(exception),
    };
  }

  abstract toPrimitives(): any;

  isVisible(): boolean {
    return true;
  }

  hasServerError(): boolean {
    return this.errors.length === 1 && this.errors[0].reason === '500';
  }

  hasErrors(): boolean {
    return !this.hasServerError() && this.errors.length > 0;
  }
}
